/**
 * greeting.ts — pure helpers for the dashboard's greeting hero per the
 * dashboard surface (Sketched in Claude Design 2026-05-10).
 *
 * Three functions, no side effects, no React. The greeting hero
 * composes them as `${greetingFor(now)}, ${firstNameOf(name)}` plus
 * the `formatDateLine(now)` eyebrow. Tests freeze time via explicit
 * Date arguments — none of these helpers should ever read `new Date()`
 * internally.
 *
 * Per the SCRUM-58 canonical rules (2026-05-09):
 *  - `useCurrentUser` exposes `{ id, email }` only — NO `name` field.
 *  - The greeting hero passes `undefined` to `firstNameOf`, which
 *    falls back to `FIRST_NAME_FALLBACK` ("there"). DO NOT derive a
 *    fake name from the email's local-part.
 */

/**
 * Time-bucket greeting per the dashboard surface (Sketched in Claude
 * Design 2026-05-10):
 *   0–4   → "Working late"
 *   5–11  → "Good morning"
 *   12–16 → "Good afternoon"
 *   17–20 → "Good evening"
 *   21–23 → "Good night"
 *
 * `locale` is reserved for future i18n (the lookup table is English-only
 * in v1 per the dashboard surface contract). Passing an invalid locale
 * does NOT change the output today — but the parameter is accepted so
 * future i18n is a non-breaking change. Any locale value (including
 * `null`, `undefined`, `'<script>'`, oversize strings) is silently
 * ignored.
 *
 * Throws `RangeError` if `date` is an invalid `Date` (e.g.
 * `new Date(NaN)`). Caller is expected to pass a valid Date — this is
 * a programming-error contract, not a user-facing fallback.
 */
export function greetingFor(date: Date, locale?: string | null): string {
  void locale; // reserved for future i18n
  if (Number.isNaN(date.getTime())) {
    throw new RangeError('greetingFor requires a valid Date');
  }
  const h = date.getHours();
  if (h < 5) return 'Working late';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  if (h < 21) return 'Good evening';
  return 'Good night';
}

/**
 * Long date eyebrow per the dashboard surface (Sketched in Claude
 * Design 2026-05-10):
 *   en-US → "Saturday, May 9"     (no year, comma implicit)
 *   fr-FR → "samedi 9 mai"
 *   ja-JP → "5月9日土曜日"
 *
 * Built on `Intl.DateTimeFormat` so locale handling is automatic. The
 * deliberate omissions (no year, no time) match the spec.
 *
 * Throws `RangeError` if `date` is an invalid `Date`. Defensively falls
 * back to the host default locale if `locale` is `null`, `undefined`,
 * or a string that `Intl.DateTimeFormat` rejects (very long, malformed,
 * or injection-shaped) — those degrade rather than throw.
 */
export function formatDateLine(date: Date, locale?: string | null): string {
  if (Number.isNaN(date.getTime())) {
    throw new RangeError('formatDateLine requires a valid Date');
  }
  const opts: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  };
  // First attempt: caller-supplied locale (if any).
  if (typeof locale === 'string' && locale.length > 0) {
    try {
      return new Intl.DateTimeFormat(locale, opts).format(date);
    } catch {
      // Fall through to host-default below.
    }
  }
  // Host-default fallback — `undefined` tells Intl to use the runtime
  // default. This always succeeds for a valid Date.
  return new Intl.DateTimeFormat(undefined, opts).format(date);
}

/**
 * First-name extraction per the dashboard surface (Sketched in Claude
 * Design 2026-05-10):
 *   const first = (user.name || "").trim().split(/\\s+/)[0] || "there";
 *
 * Returns the first whitespace-separated token of the input, OR the
 * fallback `FIRST_NAME_FALLBACK` ("there") for empty / whitespace-only
 * / null / undefined inputs. The hook returns whatever token appears
 * first — including unicode and including literal payloads like
 * `"<script>..."`. The dashboard renders this through JSX so React's
 * auto-escaping is the XSS defense, not this function.
 *
 * Always returns a non-empty string.
 */
export function firstNameOf(name: string | null | undefined): string {
  if (name === null || name === undefined) return FIRST_NAME_FALLBACK;
  const trimmed = name.trim();
  if (trimmed.length === 0) return FIRST_NAME_FALLBACK;
  const first = trimmed.split(/\s+/)[0];
  return first !== undefined && first.length > 0 ? first : FIRST_NAME_FALLBACK;
}

/** Fallback used when no name is available. Exported for tests. */
export const FIRST_NAME_FALLBACK = 'there';
